import React, { useState, useEffect, forwardRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, IconButton, Slide, useTheme,
  Divider, Avatar, Chip, TextField, alpha, CircularProgress,
  Grow, Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion'; // Add framer-motion to your dependencies if not already installed

// Slide transition for dialog entry
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MessageDialog = ({ 
  open, 
  onClose,
  message,
  loading = false,
  onReply,
  disableActions = false
}) => {
  const theme = useTheme();
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState('');
  const [sending, setSending] = useState(false);
  const [animateContent, setAnimateContent] = useState(false);

  // Start content animation after dialog opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setAnimateContent(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setAnimateContent(false);
    }
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setReplyText('');
      setReplyError('');
      setSending(false);
    }
  }, [open]);

  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
    if (e.target.value.trim()) {
      setReplyError('');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      setReplyError('Reply text is required');
      return;
    }

    setSending(true);
    try {
      await onReply(replyText);
      setReplyText('');
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  if (!message && !loading) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={disableActions ? null : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[10],
          backgroundImage: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          overflow: 'hidden'
        }
      }}
    >
      {/* Animated Header */}
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          background: `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Avatar 
              sx={{ 
                bgcolor: message?.isBroadcast ? 'info.main' : 'primary.main',
                mr: 2,
                boxShadow: theme.shadows[3]
              }}
            >
              {message?.isBroadcast ? <CampaignIcon /> : <EmailIcon />}
            </Avatar>
          </motion.div>
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Typography variant="h6" component="div">
              {loading ? 'Loading Message...' : message?.subject}
            </Typography>
          </motion.div>
        </Box>
        
        {!disableActions && (
          <IconButton 
            edge="end" 
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Message Header with animation */}
            <Fade in={animateContent} timeout={800}>
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                  {message?.isBroadcast && (
                    <Chip
                      label="Broadcast"
                      size="small"
                      color="info"
                      icon={<CampaignIcon fontSize="small" />}
                      sx={{ 
                        borderRadius: '16px',
                        '& .MuiChip-label': { px: 1.5 },
                        '& .MuiChip-icon': { ml: 0.5 }
                      }}
                    />
                  )}
                  
                  <Chip
                    label={message?.isRead ? "Read" : "Unread"}
                    size="small"
                    color={message?.isRead ? "success" : "error"}
                    variant="outlined"
                    sx={{ borderRadius: '16px' }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    {message?.createdAt ? new Date(message.createdAt).toLocaleString() : ''}
                  </Typography>
                </Box>
              </Box>
            </Fade>
            
            {/* Message Content with animation */}
            <Grow in={animateContent} timeout={1000}>
              <Box 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  boxShadow: theme.shadows[1]
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {message?.text}
                </Typography>
              </Box>
            </Grow>
            
            {/* Reply Form with animation */}
            <Fade in={animateContent} timeout={1200}>
              <Box>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Quick Reply
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type your reply here..."
                  value={replyText}
                  onChange={handleReplyChange}
                  error={!!replyError}
                  helperText={replyError}
                  disabled={sending || disableActions}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }
                  }}
                />
              </Box>
            </Fade>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        background: `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.5)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
      }}>
        <Button onClick={onClose} color="inherit" disabled={disableActions}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleSendReply}
          disabled={sending || loading || !replyText.trim() || disableActions}
          sx={{ 
            borderRadius: 2, 
            px: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            '&:hover': {
              background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
            },
            boxShadow: theme.shadows[2],
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {sending ? 'Sending...' : 'Send Reply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageDialog;